import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '@heroui/react'
import { sameDocument } from '../../layout/editing'
import { api } from '../../lib/api'
import { problemsAt } from '../../lib/equipment'
import { passUnsaved } from '../../lib/unsaved'
import type { Branch, BranchSettings, EquipmentProfileRow, OperationProfileRow, ProfileProblem } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useSession } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { AutoEnd, DayStart, HoursEditor, clock, dayEndSummary, hoursSummary, withDayEnd } from '../Branches'
import { FormSection, InnerPage, Notice, type Crumb } from '../settingsKit'
import { AskBar, Pick } from '../setupKit'
import type { BranchDraft } from './useBranchDraft'
import '../../styles/equipment.css'
import '../../styles/branchpage.css'

/** The branch's profiles and business day. */
export function BranchSetup({
  branch,
  d,
  canWrite,
  onHours,
}: {
  branch: Branch
  d: BranchDraft
  canWrite: boolean
  /** Opens the business day's page. */
  onHours: () => void
}) {
  const { say } = useStaffText()
  const session = useSession()
  const [operations, setOperations] = useState<OperationProfileRow[]>([])
  const [equipments, setEquipments] = useState<EquipmentProfileRow[]>([])

  const draft = d.draft
  const opShown = d.pendingTarget?.operationProfileId ?? draft?.operationProfileId ?? ''
  const eqShown = d.pendingTarget?.equipmentProfileId ?? draft?.equipmentProfileId ?? ''

  const organisation = session?.identity.owner ? branch.tenantId : undefined
  useEffect(() => {
    api
      .operationProfiles(organisation)
      .then((r) => setOperations(r.profiles))
      .catch(() => setOperations([]))
  }, [organisation])

  useEffect(() => {
    if (!opShown) return
    api
      .equipmentProfiles(opShown)
      .then((r) => setEquipments(r.equipmentProfiles))
      .catch(() => setEquipments([]))
  }, [opShown])

  if (!draft) return null

  const settings = draft.settings
  const hours = hoursSummary(settings.openingHours ?? [], say)
  const locked = !canWrite || d.busy
  const firstProblem = d.problems.find((p) => !p.warning)
  const pending = d.pending

  const rows: Array<{ label: string; at: string; value: ReactNode }> = [
    {
      label: say('eq.branch.operation'),
      at: 'operationProfileId',
      value: (
        <Pick
          size="sm"
          className="eq-setup__pick"
          label={say('eq.branch.operation')}
          value={opShown}
          disabled={locked}
          onChange={(id) => (id === draft.operationProfileId ? d.cancelSwitch() : void d.switchProfiles(id))}
          options={operations.map((p) => ({ value: p.id, label: p.name }))}
        />
      ),
    },
    {
      label: say('eq.branch.equipment'),
      at: 'equipmentProfileId',
      value: (
        <Pick
          size="sm"
          className="eq-setup__pick"
          label={say('eq.branch.equipment')}
          value={eqShown}
          disabled={locked}
          onChange={(id) =>
            opShown === draft.operationProfileId && id === draft.equipmentProfileId
              ? d.cancelSwitch()
              : void d.switchProfiles(opShown, id)
          }
          options={equipments.map((p) => ({ value: p.id, label: p.name }))}
        />
      ),
    },
    {
      label: say('admin.hours.openingHours'),
      at: 'settings.openingHours',
      value: hours.length ? hours.join(', ') : say('org.br.noHoursState'),
    },
    { label: say('org.br.dayStarts'), at: 'settings.rolloverMinute', value: clock(settings.rolloverMinute) },
    {
      label: say('org.br.dayEnds'),
      at: 'settings.autoEndOfDayMin',
      value: dayEndSummary(settings, settings.openingHours ?? [], say),
    },
  ]

  return (
    <FormSection
      id="bp-settings-title"
      icon="clock"
      title={say('bp.settings')}
      line={say('settings.branch.setupLine')}
      aside={
        canWrite ? (
          <Button variant="secondary" size="sm" isDisabled={locked} onPress={onHours}>
            <Icon name="clock" size={16} /> {say('bp.hours')}
          </Button>
        ) : undefined
      }
    >

      {d.error && <Notice className="bp-notice">{d.error}</Notice>}
      {d.stale && (
        <Notice tone="warning" className="bp-notice">
          {say('eq.branch.overwrote', { who: d.stale })}
        </Notice>
      )}
      {firstProblem && <Notice className="bp-notice">{firstProblem.message}</Notice>}

      <dl className="bp-settings">
        {rows.map((r) => (
          <div key={r.at} className="bp-setting">
            <dt>{r.label}</dt>
            <dd>
              {r.value}
              <Pinned problems={problemsAt(d.problems, r.at)} />
            </dd>
          </div>
        ))}
      </dl>

      {pending && (
        <AskBar
          className="eq-carry"
          title={say('eq.branch.carry', { kept: pending.kept, created: pending.created })}
          actions={
            <>
              <Button variant="ghost" size="sm" onPress={d.cancelSwitch}>
                {say('common.cancel')}
              </Button>
              <Button variant="primary" size="sm" isDisabled={d.busy} onPress={() => void d.applySwitch()}>
                {say('eq.branch.apply')}
              </Button>
            </>
          }
        >
          {(pending.dropped.length > 0 || pending.clearedDesigns.length > 0 || pending.notMade.length > 0) && (
            <span className="eq-carry__text">
              {pending.dropped.length > 0 && (
                <span>{say('eq.branch.carryDrops', { codes: pending.dropped.map((i) => i.code).join(', ') })}</span>
              )}
              {pending.clearedDesigns.length > 0 && (
                <span>
                  {say('eq.branch.carryDesigns', { n: pending.clearedDesigns.length, count: pending.clearedDesigns.length })}
                </span>
              )}
              {pending.notMade.map((m) => (
                <span key={m.unit}>{say('eq.branch.carryNotMade', { unit: m.unit, count: m.count })}</span>
              ))}
            </span>
          )}
        </AskBar>
      )}

      {d.report && (
        <AskBar
          className="eq-carry"
          role="status"
          actions={
            <Button isIconOnly variant="ghost" size="sm" aria-label={say('ui.close')} onPress={d.dismissReport}>
              <Icon name="close" size={16} />
            </Button>
          }
        >
          <span className="eq-carry__text">
            {d.report.dropped.length > 0 && <span>{say('eq.branch.didDrop', { codes: d.report.dropped.join(', ') })}</span>}
            {d.report.clearedDesigns.length > 0 && (
              <span>
                {say('eq.branch.didDesigns', { n: d.report.clearedDesigns.length, count: d.report.clearedDesigns.length })}
              </span>
            )}
            {d.report.notMade.map((m) => (
              <span key={m.unit}>{say('eq.branch.didNotMake', { unit: m.unit, count: m.count })}</span>
            ))}
          </span>
        </AskBar>
      )}

    </FormSection>
  )
}

function Pinned({ problems }: { problems: ProfileProblem[] }) {
  const shown = problems.filter((p) => !p.warning)
  if (!shown.length) return null
  return (
    <>
      {shown.map((p, i) => (
        <span key={i} className="eq-problem">
          {p.message}
        </span>
      ))}
    </>
  )
}

/** Opening hours, day start and automatic day end; Save is live and returns to the branch. */
export function DayPage({
  d,
  crumbs,
  onClose,
  flash,
}: {
  d: BranchDraft
  crumbs: Crumb[]
  onClose: () => void
  flash: (text: string) => void
}) {
  const { say } = useStaffText()
  const value = d.draft!.settings
  const [v, setV] = useState<BranchSettings>({ ...value, openingHours: value.openingHours ?? [] })
  const [refused, setRefused] = useState('')
  const hoursBad = v.openingHours.some((h) => h.close < 1440 && h.close <= h.open)
  const dirty = !sameDocument(v, { ...value, openingHours: value.openingHours ?? [] })
  return (
    <InnerPage
      before={crumbs}
      title={say('org.br.businessDay')}
      dirty={dirty}
      valid={!hoursBad && dirty}
      submitLabel={say('common.save')}
      busy={d.busy}
      error={refused || undefined}
      onClose={onClose}
      onSubmit={async () => {
        setRefused('')
        const why = await d.commit((x) => ({ ...x, settings: v }))
        if (why) return setRefused(why)
        flash(say('common.saved'))
        passUnsaved()
        onClose()
      }}
    >
      <HoursEditor value={v.openingHours} onChange={(openingHours) => setV((x) => ({ ...x, openingHours }))} />
      <DayStart value={v.rolloverMinute} onChange={(rolloverMinute) => setV((x) => ({ ...x, rolloverMinute }))} />
      <AutoEnd
        value={v}
        hasHours={v.openingHours.length > 0}
        onChange={(off, min) => setV((x) => withDayEnd(x, off, min))}
      />
    </InnerPage>
  )
}
